import { useCallback, useRef } from "react";
import { Activity } from "lucide-react";
import type { LatestReading, Reading } from "@spf/contracts";
import type { StreamStatus } from "@spf/api-client";
import { useLiveTelemetry } from "../lib/useLiveTelemetry.js";
import { useFreshness, type FreshnessVerdict } from "../lib/useFreshness.js";
import { PowerFlow } from "../components/flow/PowerFlow.js";
import { NodeTiles } from "../components/live/NodeTiles.js";
import { Card } from "../components/Card.js";
import { Freshness } from "../components/Freshness.js";
import { Empty, Failed, Loading, buttonClass } from "../components/ViewState.js";
import { formatAge, formatDateTime, formatUnit } from "../lib/format.js";
import { counters } from "../lib/counters.js";

/**
 * What the system is doing right now: the first of the jobs in
 * docs/REQUIREMENTS.md section 1.
 *
 * Values arrive over a held-open connection rather than by polling, so the
 * page changes when the inverter reports rather than on a timer that is either
 * too slow to be live or too fast to be kind to a shared host.
 *
 * The panels are one of the diagram's sources since slice 5c, once their
 * fields were verified against a generating array (docs/DECISIONS/0028).
 */
export function LivePage() {
  const { status, latest, retry } = useLiveTelemetry();
  const heading = useRef<HTMLHeadingElement>(null);

  // Retrying unmounts the control that was just used -- the status goes back to
  // `connecting`, which is deliberately not alarmed on -- so without this a
  // keyboard reader who acts on the banner is returned to the top of the tab
  // order, on the view the app opens on. Focus moves to the heading of the
  // region they were already in.
  const retryHere = useCallback(() => {
    retry();
    heading.current?.focus();
  }, [retry]);
  // Held here rather than inside the sentence that prints it, so the diagram
  // and the "reading taken" line move with it. It also re-renders this view
  // once a second, which is what keeps those two from drifting apart.
  const freshness = useFreshness(latest);

  return (
    <section aria-labelledby="live-heading" className="flex flex-col gap-5">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">
            <Activity size={14} aria-hidden="true" className="text-accent-2-ink" />
            Live power bus
          </p>
          <h2
            id="live-heading"
            ref={heading}
            tabIndex={-1}
            className="text-3xl font-bold tracking-tight text-ink outline-none sm:text-4xl"
          >
            Live
          </h2>
        </div>
        {latest !== null && latest.reading !== null && <Ticker reading={latest.reading} />}
      </div>

      {latest === null ? (
        <Card title="Connection">
          <Connecting status={status} onRetry={retryHere} />
        </Card>
      ) : (
        <div className="flex flex-col gap-5">
          <Connection status={status} value={latest} freshness={freshness} onRetry={retryHere} />
          <ReadingBody value={latest} freshness={freshness} />
        </div>
      )}
    </section>
  );
}

/**
 * The design's live ticker beside the title: three figures from the mains side
 * of the inverter, on the same row as the heading.
 *
 * The design's third entry is a grid status of "Synchronized" or "Islanded
 * (<8ms)", switched by its outage simulator. There is no register in this
 * protocol that reports either, so the third figure is the mains voltage the
 * inverter actually measures, and whether the grid is carrying anything is
 * said by the flow diagram below in words.
 */
function Ticker({ reading }: { reading: Reading }) {
  return (
    <div className="tabular flex flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl border border-line bg-card p-2 text-xs">
      <Tick label="Bus frequency" value={formatUnit(reading.gridFrequencyHz, "Hz")} />
      <span aria-hidden="true" className="h-6 w-px bg-line" />
      <Tick label="Output voltage" value={formatUnit(reading.outputVoltageV, "V")} />
      <span aria-hidden="true" className="h-6 w-px bg-line" />
      <Tick label="Mains voltage" value={formatUnit(reading.gridVoltageV, "V")} />
    </div>
  );
}

function Tick({ label, value }: { label: string; value: string }) {
  return (
    <p className="px-3 py-1">
      <span className="block text-[10px] text-ink-muted">{label}</span>
      <span className="font-bold text-ink">{value}</span>
    </p>
  );
}

/**
 * Loading and error, before anything has ever arrived.
 *
 * The two failures are told apart because an error state has to say what to do
 * next (docs/REQUIREMENTS.md section 4), and "the server may be unreachable"
 * sends someone with too many tabs open to look for the wrong problem.
 */
function Connecting({ status, onRetry }: { status: StreamStatus; onRetry: () => void }) {
  if (status === "crowded") {
    return <Failed message={`${CROWDED} Then try again.`} onRetry={onRetry} />;
  }
  if (status === "offline") {
    return (
      <Failed
        message="Could not connect for live updates. The server may be unreachable."
        onRetry={onRetry}
      />
    );
  }
  return <Loading label="Connecting for live updates…" />;
}

const OFFLINE = "Not receiving live updates, and trying to reconnect.";
const CROWDED =
  "Live updates have stopped: this account already has as many of them open as it allows. " +
  "Close another tab or device showing this page.";

/**
 * The connection's own state, which is not the same question as the reading's
 * age. Once it is down the page stops presenting a live age and says when the
 * last reading actually was instead.
 *
 * `connecting` is deliberately not alarmed on. It is the ordinary end of every
 * stream -- the server closes one when its access token runs out, and the
 * client is back inside a second -- so raising an alert on it would put
 * "not receiving live updates" on a perfectly healthy dashboard once every
 * token lifetime, which is how a reader learns to ignore the warning that
 * matters. An attempt that actually fails reports `offline` or `crowded`. The
 * age stays honest across the gap either way, because Freshness advances it.
 */
function Connection({
  status,
  value,
  freshness,
  onRetry,
}: {
  status: StreamStatus;
  value: LatestReading;
  freshness: FreshnessVerdict;
  onRetry: () => void;
}) {
  if (status === "live" || status === "connecting") {
    return <Freshness value={value} freshness={freshness} />;
  }

  // The retry is offered here as well as before the first reading. It matters
  // most for `crowded`, whose whole message is to close another tab: without
  // it the reader does the one thing they were asked to do and then has to sit
  // out a backoff that may have doubled to half a minute.
  return (
    <div className="flex flex-col items-start gap-3">
      <p
        className="rounded-2xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger-ink"
        role="status"
      >
        {status === "crowded" ? CROWDED : OFFLINE} {lastReading(value, freshness)}
      </p>
      <button type="button" onClick={onRetry} className={buttonClass}>
        Try again
      </button>
    </div>
  );
}

/**
 * When the last reading actually was, on the same clock as every other age
 * on this page.
 *
 * This banner and the definition list below it are both on screen while the
 * connection is down, describing the same reading. Reading this one off the
 * browser's clock and that one off the server's age gave them two different
 * answers on any device whose clock is off -- on a page whose whole subject
 * is whether a number is current.
 */
function lastReading(value: LatestReading, freshness: FreshnessVerdict): string {
  const at = value.reading?.time;
  if (at === undefined) return "Nothing has arrived yet.";
  if (freshness.ageSeconds === null) return `The last reading was at ${formatDateTime(at)}.`;

  return `The last reading was ${formatAge(freshness.ageSeconds)}, at ${formatDateTime(at)}.`;
}

function ReadingBody({ value, freshness }: { value: LatestReading; freshness: FreshnessVerdict }) {
  if (value.reading === null) {
    return (
      <Empty>
        No telemetry yet. Once the inverter&rsquo;s dongle reports, this page updates on its own
        within about five minutes.
      </Empty>
    );
  }

  const { reading } = value;
  return (
    <>
      <Card title="Power flow">
        {/* The clock's verdict, not the server's frozen one: the picture must
            not stay bright under a sentence saying the reading is stale. */}
        <PowerFlow reading={reading} stale={freshness.stale} />
      </Card>

      <NodeTiles reading={reading} />

      <Card title="Now">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          <Row label="Battery charge">
            {reading.socPct}% at {formatUnit(reading.batteryVoltageV, "V")}
          </Row>
          <Row label="Grid voltage">{formatUnit(reading.gridVoltageV, "V")}</Row>
          <Row label="Inverter temperature">{formatUnit(reading.inverterTempC, "°C")}</Row>
          <Row label="Reading taken">
            {/* The same clock as the sentence above: the server's age moved on by
              however long ago it said it. Reading this one off the browser's
              clock instead gave the same reading two ages on a handset whose
              clock was off. */}
            {freshness.ageSeconds === null
              ? formatDateTime(reading.time)
              : `${formatAge(freshness.ageSeconds)} (${formatDateTime(reading.time)})`}
          </Row>
        </dl>
      </Card>

      <Detail reading={reading} />

      {reading.isBuffered && (
        <p className="rounded-card border border-line bg-card p-6 text-sm text-ink-muted">
          This record was stored by the dongle during an outage and delivered late, so it is not a
          picture of this moment.
        </p>
      )}
    </>
  );
}

/**
 * The wider fields slice 5A decoded and stored, on a screen for the first
 * time. Every one is nullable, and null means "not recorded" rather than
 * zero -- either the row predates the column or it is older than the 90-day
 * raw_frames retention and can never be backfilled. `formatUnit` prints an em
 * dash for null, which is why none of these renders as 0.
 */
function Detail({ reading }: { reading: Reading }) {
  return (
    <Card title="Everything decoded">
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        <Row label="Grid frequency">{formatUnit(reading.gridFrequencyHz, "Hz")}</Row>
        <Row label="Output voltage">{formatUnit(reading.outputVoltageV, "V")}</Row>
        <Row label="Output frequency">{formatUnit(reading.outputFrequencyHz, "Hz")}</Row>
        {/* "Load level", not "Load": the browser suite matches the exact text
            "Load" on the Telemetry view, and a percentage of the nameplate is
            a different quantity from load power anyway. */}
        <Row label="Load level">{formatUnit(reading.loadPercent, "%")}</Row>
        <Row label="DC-DC temperature">{formatUnit(reading.dcdcTempC, "°C")}</Row>
        <Row label="Fan speed">{formatUnit(reading.fanSpeedPct, "%")}</Row>
        {(["today", "total"] as const).flatMap((span) =>
          counters(reading, span).map((counter) => (
            <Row
              key={`${counter.label} ${span}`}
              label={`${counter.label} ${span === "today" ? "today" : "in total"}`}
            >
              {formatUnit(counter.value, "kWh")}
            </Row>
          )),
        )}
      </dl>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line pb-2">
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="tabular text-sm font-medium text-ink">{children}</dd>
    </div>
  );
}
